import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Heart, Users, Camera, MapPin, Star, Sparkles, Dog,
  PartyPopper, Smile, PawPrint, ArrowRight, Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Images
const TOBY_IMAGE = "https://customer-assets.emergentagent.com/job_tradie-leads-1/artifacts/92iyvmuc_Tobi%20CBO%20Puppoto.jpg";
const KOKO_IMAGE = "https://customer-assets.emergentagent.com/job_tradie-leads-1/artifacts/1wnoe6hu_Koko%20Chief%20Cuddle%20Officer%20and%20Flirt.png";

const PuppotoJoin = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setSubmitting(true);
    try {
      await fetch(`${API_URL}/api/puppoto/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      setSubmitted(true);
      toast.success('Woof! You\'re on the list!');
    } catch (err) {
      toast.error('Oops! Try again');
    } finally {
      setSubmitting(false);
    }
  };

  const features = [
    { icon: Heart, title: "Pup Match", desc: "Tinder-style swiping for playdates", color: "from-pink-400 to-rose-500" },
    { icon: Camera, title: "Photo Feeds", desc: "Share adorable moments", color: "from-purple-400 to-indigo-500" },
    { icon: Users, title: "Dog Park Buddies", desc: "Find friends nearby", color: "from-blue-400 to-cyan-500" },
    { icon: PartyPopper, title: "Celebrations", desc: "Birthdays & gotcha days", color: "from-yellow-400 to-orange-500" },
    { icon: MapPin, title: "Local Services", desc: "Vets, groomers & more", color: "from-green-400 to-emerald-500" },
    { icon: Star, title: "Dog Shows", desc: "Events near you", color: "from-amber-400 to-orange-500" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-100 via-orange-50 to-yellow-100 overflow-hidden">
      {/* Floating Paw Prints */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute text-4xl opacity-20"
            initial={{ y: '100vh', x: Math.random() * 100 + 'vw' }}
            animate={{ 
              y: '-10vh',
              rotate: 360,
              transition: {
                duration: 10 + Math.random() * 10,
                repeat: Infinity,
                ease: 'linear',
                delay: Math.random() * 5
              }
            }}
          >
            🐾
          </motion.div>
        ))}
      </div>

      <div className="relative z-10">
        {/* Hero */}
        <div className="container mx-auto px-4 pt-8 pb-16">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            {/* Toby & Koko */}
            <div className="flex justify-center items-center gap-4 mb-6">
              <motion.img 
                src={TOBY_IMAGE} 
                alt="Toby" 
                className="w-20 h-20 md:w-28 md:h-28 rounded-full border-4 border-white shadow-xl object-cover"
                animate={{ rotate: [0, -5, 5, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
              <div className="text-4xl">❤️</div>
              <motion.img 
                src={KOKO_IMAGE} 
                alt="Koko" 
                className="w-20 h-20 md:w-28 md:h-28 rounded-full border-4 border-white shadow-xl object-cover"
                animate={{ rotate: [0, 5, -5, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            </div>

            <h1 className="text-4xl md:text-6xl font-black mb-4">
              <span className="bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500 bg-clip-text text-transparent">
                Love Your Doggy?
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-700 max-w-2xl mx-auto mb-2">
              Find them some friends! 🐕
            </p>
            <p className="text-lg text-gray-600 max-w-xl mx-auto">
              Playdates, park buddies, and new canine friends are just a click away!
            </p>
          </motion.div>

          {/* Main Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="max-w-xl mx-auto mt-10"
          >
            <div className="bg-white rounded-3xl shadow-2xl p-8 border-4 border-orange-200">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Dog className="w-8 h-8 text-orange-500" />
                <h2 className="text-3xl font-bold text-gray-800">Puppoto</h2>
              </div>
              
              <p className="text-center text-gray-600 mb-6">
                The social playground for dogs! Coming <strong>May 2026</strong>
              </p>

              {submitted ? (
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="text-center py-6"
                >
                  <div className="text-6xl mb-4">🎉</div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">Woof! You're In!</h3>
                  <p className="text-gray-600">
                    We'll bark at you when we launch! 🐕
                  </p>
                  <div className="mt-6 p-4 bg-orange-50 rounded-xl">
                    <p className="text-sm text-orange-700">
                      <strong>Pro tip:</strong> Tell your dog-loving friends too!
                    </p>
                  </div>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="w-full px-5 py-4 text-lg rounded-xl border-2 border-orange-200 focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-200"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-4 text-xl bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all"
                  >
                    {submitting ? (
                      'Joining...'
                    ) : (
                      <>
                        Join the Pack! <PawPrint className="w-6 h-6 ml-2" />
                      </>
                    )}
                  </Button>
                  <p className="text-center text-gray-500 text-sm">
                    Free to join • No spam, we promise! 🐾
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </div>

        {/* Features */}
        <div className="bg-white/70 backdrop-blur-sm py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-800 mb-10">
              What's Waiting For Your Pup? 🐕
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
              {features.map((feature, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + i * 0.1 }}
                  className="bg-white rounded-2xl p-4 md:p-6 shadow-lg hover:shadow-xl transition-all text-center"
                >
                  <div className={`w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br ${feature.color} rounded-xl flex items-center justify-center mx-auto mb-3`}>
                    <feature.icon className="w-6 h-6 md:w-7 md:h-7 text-white" />
                  </div>
                  <h3 className="font-bold text-gray-800 mb-1">{feature.title}</h3>
                  <p className="text-sm text-gray-500">{feature.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="container mx-auto px-4 py-16 text-center">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <p className="text-xl text-gray-700 mb-4">
              🇦🇺 Made with love in Australia
            </p>
            <p className="text-gray-500">
              A Lionheart Digital Production
            </p>
            <div className="mt-8 flex justify-center gap-2 text-3xl">
              <span>🐕</span>
              <span>🐩</span>
              <span>🦮</span>
              <span>🐕‍🦺</span>
              <span>🐶</span>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PuppotoJoin;
